import { resolveParallel } from '@/helpers/promises'
import { resolveSerial } from '@/helpers/promises'
import type {
  ConfigFolderGetter,
  ConfigFileGetter,
  ConfigFileSpec,
  PageMeta,
} from '@/types'
import {
  defaultGuide,
  defaultPage,
  type Folder,
  type Guide,
  type Link,
  type Page,
  type SidebarItem,
  SidebarType,
} from '@scalar-org/entities/project'
import type {
  ConfigGuide,
  ConfigSidebarItem,
  ConfigSidebarPage,
} from '@/schema'
import matter from 'gray-matter'
import { nanoid } from 'nanoid'
import picomatch from 'picomatch'
import { join } from 'node:path'

/** Resolve a complete guide object and its page content from a Scalar config file
 *
 * - Searches for all relevant files and loads their content to strings
 * - Creates the guide sidebar from the config data and file content
 * - Returns the guide object and the documents
 */
export async function resolveGuide<F extends ConfigFileSpec = ConfigFileSpec>({
  config,
  fileGetter,
  folderGetter,
  parallel,
  root,
}: {
  config: ConfigGuide
  fileGetter: ConfigFileGetter<F>
  folderGetter: ConfigFolderGetter<F>
  parallel: boolean
  root: string
}) {
  const documents: Record<string, string> = {}
  const documentPaths: Record<string, string> = {}
  const items: Guide['sidebar']['items'] = {}

  /** Guide config objects have an include and exclude glob pattern to match folder files */
  const filter = globFilterFactory(config)

  /** Create a guide object from the files and guide config entry */
  const guide: Guide = {
    ...defaultGuide(),
    description: config.description || '',
    title: config.name,
    sidebar: {
      children:
        'folder' in config
          ? await getGuideSidebarElementsFromFolder({
              folderPath: config.folder,
              items,
              documents,
              documentPaths,
              /** Filter out files based on their path before fetching */
              fileGetter: (f) => (filter(f.path) ? fileGetter(f) : null),
              folderGetter,
              parallel,
              root,
            })
          : await getGuideSidebarElements({
              items: items,
              children: config.sidebar,
              /** typecast is safe as path is always included */
              fileGetter: fileGetter as ConfigFileGetter<{ path: string }>,
              documents,
              documentPaths,
              parallel,
              root,
            }),
      items,
    },
  }

  return { guide, documents, documentPaths }
}

/**
 * Recursive function to populate the guide sidebar a Scalar config file.
 * Accepts a flexible fileGetter to handle different environments
 *
 * - Mutates the `guide.sidebar.items` object in place due to the recursive nature of this compute
 * - Mutates the `documents` object in place to store the content of the page.
 * - Returns the of item uids for the `guide.sidebar.children` entry
 */
export const getGuideSidebarElements = async ({
  children,
  items,
  fileGetter,
  documents,
  documentPaths,
  parallel,
  root,
}: {
  /** List of elements to convert to children uid lists */
  children: ConfigSidebarItem[]
  /** Guide sidebar items list that is mutated in place to handle recursion. Map of item uids to sidebar items */
  items: Partial<Record<string, SidebarItem>>
  /** Map of page uids to the file content that is mutated in place to handle recursion  */
  documents: Record<string, string>
  /** Map of page uids to the file path that is mutated in place to handle recursion  */
  documentPaths: Record<string, string>
  /** Request function to get a file from a path string */
  fileGetter: ConfigFileGetter<{ path: string }>
  parallel?: boolean
  root: string
}): Promise<string[]> => {
  async function resolveEntry(entry: ConfigSidebarItem) {
    // ---------------------------------------------------------------------------
    if (entry.type === 'page' && entry.path.endsWith('.md')) {
      const file = await fileGetter({ path: join(root, entry.path) })

      const { page, content } = await pageFromFile(file, entry)

      // If there are nested page children we need to run recursively
      if (entry.children) {
        page.children = await getGuideSidebarElements({
          items,
          children: entry.children,
          fileGetter,
          documents,
          documentPaths,
          root,
        })
      }
      // Put the new item into the main items reference and return the uid
      items[page.uid] = page
      documents[page.uid] = content
      documentPaths[page.uid] = entry.path

      /** Return the UID to allow correct ordering of children after promise resolution */
      return page.uid
    }
    // ---------------------------------------------------------------------------
    if (entry.type === 'link') {
      const link: Link = {
        uid: nanoid(),
        title: entry.name,
        show: true,
        type: SidebarType.Link,
        link: entry.url,
        icon: entry.icon ? { src: entry.icon } : undefined,
      }

      items[link.uid] = link

      /** Return the UID to allow correct ordering of children after promise resolution */
      return link.uid
    }
    // ---------------------------------------------------------------------------
    if (entry.type === 'folder') {
      const folder: Folder = {
        uid: nanoid(),
        title: entry.name,
        show: true,
        type: SidebarType.Folder,
        children: entry.children
          ? await getGuideSidebarElements({
              items,
              children: entry.children,
              fileGetter,
              documents,
              documentPaths,
              root,
            })
          : [],
        icon: entry.icon ? { src: entry.icon } : undefined,
      }
      items[folder.uid] = folder

      /** Return the UID to allow correct ordering of children after promise resolution */
      return folder.uid
    }
  }

  /** In some cases (Github fetches) we want to avoid rate-limiting and serialize the requests */
  const childUids = parallel
    ? await resolveParallel(children.map(resolveEntry))
    : await resolveSerial(children.map(resolveEntry))

  return childUids.filter((uid) => uid !== undefined)
}

/**
 * If a folder is specified for a guide we traverse the directory
 * and create sidebar entries for every markdown file found
 *
 * Subfolder are added as sidebar folders when they have at least 1 entry
 * Folders need to be ignored when empty so that glob matching works as expected
 */
export const getGuideSidebarElementsFromFolder = async <
  F extends ConfigFileSpec = ConfigFileSpec,
>({
  folderPath,
  folderGetter,
  fileGetter,
  items,
  documents,
  documentPaths,
  parallel,
  root,
}: {
  /** Folder path to determine children at current level from */
  folderPath: string
  /** Object that is mutated in place and populated with the sidebar item objects */
  items: Partial<Record<string, SidebarItem>>
  /**
   * Request function to get a file from a SHA string
   * Null may be returned to allow for glob matching and file ignores
   */
  fileGetter: ConfigFileGetter<F>
  /** Folder getter to return a list of files from a github folder */
  folderGetter: ConfigFolderGetter<F>
  /** Document record that is mutated in place */
  documents: Record<string, string>
  /** Map of page uids to the file path that is mutated in place to handle recursion  */
  documentPaths: Record<string, string>
  parallel?: boolean
  root: string
}) => {
  // Get a list of files and folders back
  const elements = await folderGetter(folderPath)

  const childUids: string[] = []

  async function resolveEntry(entry: (typeof elements)[number]) {
    // ---------------------------------------------------------------------------
    if (entry.type === 'file') {
      const file = await fileGetter({
        ...entry,
        path: join(root, entry.path),
      })
      if (file) {
        const { page, content } = await pageFromFile(file)

        // Put the new item into the main items reference and return the uid
        items[page.uid] = page
        childUids.push(page.uid)
        documents[page.uid] = content
        documentPaths[page.uid] = entry.path
      }
    }
    // ---------------------------------------------------------------------------
    if (entry.type === 'dir') {
      const children = await getGuideSidebarElementsFromFolder({
        folderPath: entry.path,
        folderGetter,
        fileGetter,
        items,
        documents,
        documentPaths,
        root,
      })

      // In folder mode we only add folders if they have children
      if (children.length) {
        const folder: Folder = {
          uid: nanoid(),
          title: prettyFilename(entry.name),
          show: true,
          type: SidebarType.Folder,
          children,
        }
        items[folder.uid] = folder
        childUids.push(folder.uid)
      }
    }
  }

  /** In some cases (Github fetches) we want to avoid rate-limiting and serialize the requests */
  if (parallel) {
    await resolveParallel(elements.map(resolveEntry))
  } else {
    await resolveSerial(elements.map(resolveEntry))
  }

  return childUids
}

/** Format a page entry from a markdown file */
async function pageFromFile(file: string | null, entry?: ConfigSidebarPage) {
  const { content, meta } = getPageMeta(file ?? '')

  const page: Page = {
    ...defaultPage(),
    // Preference to metadata
    description: meta.description || entry?.description || '',
    backgroundImage: meta.image,
    icon: entry?.icon ? { src: entry.icon } : undefined,
    /**
     * For the page title the priority order is:
     * 1. First h1 on the page (will override any other config)
     * 2. Title specified in frontmatter
     * 3. Name specified in scalar config
     * 4. Fallback to the filename when we have nothing else
     */
    title: meta.title || entry?.name || prettyFilename(entry?.path ?? ''),
    children: [],
  }

  return { page, content }
}

/**
 * Extract the page title and description from the page  content.
 *
 * Priority is given to H1 tags and page frontmatter
 */
export function getPageMeta(markdown: string) {
  const { data: meta, content } = matter(markdown) as {
    data: PageMeta
    content: string
  }

  /** If there is an H1 tag we use the first one found for the page title */
  if (!meta.title) {
    // Regex to match all level 1 headings
    const h1Match = /^#(?!#)(.*)/gm
    /** Grab the first 3000 characters to avoid regex performance issues */
    const titleResult = [...markdown.slice(0, 3000).matchAll(h1Match)]
    meta.title = titleResult?.[0]?.[1]?.trim?.()
  }

  return {
    meta,
    content,
  }
}

/**
 * Returns a pretty filename from a path string
 *
 * - Remove path slashes
 * - Remove file extension
 * - Replace dashes and underscores with spaces
 */
export function prettyFilename(filename: string): string {
  const name =
    filename
      .split('/')
      .at(-1)
      ?.split('.')[0]
      .replaceAll('-', ' ')
      .replaceAll('_', ' ') ?? ''

  return name[0].toUpperCase() + name.slice(1)
}

/** Returns false when a file should be excluded based on its path and the guide config options */
export const globFilterFactory = (
  guideConfig: ConfigGuide,
): ((p: string) => boolean) => {
  if (!('folder' in guideConfig)) return () => true

  const shouldInclude = guideConfig.include
    ? picomatch(guideConfig.include)
    : () => true
  const shouldExclude = guideConfig.exclude
    ? picomatch(guideConfig.exclude)
    : () => false

  return (p: string) => shouldInclude(p) && !shouldExclude(p)
}
